import React from 'react';
import { Helmet } from 'react-helmet-async';
import type { Selector } from 'react-redux';
import { useSelector } from 'react-redux';
import URL from 'url-parse';

import { FeatureFlag, RoutingKey } from '@swf/core/enums';
import useFeature from '@swf/core/hooks/useFeature';
import type { RootState } from '@swf/core/store';
import { select } from '@swf/core/store';

const defaultHrefLang = 'x-default';

const AlternateLinks: React.FC = () => {
  const { href } = useSelector(select.location.get);
  const locales = useSelector(select.site.getLocales);
  const strippedPathname = useSelector(select.location.stripLocale as Selector<RootState, string>);
  const alternateTags = useSelector(select.seo.get);
  const homepageOnlyAlternatesEnabled = useFeature(FeatureFlag.HomepageOnlyAlternates);
  const crossDomainAlternatesEnabled = useFeature(FeatureFlag.CrossDomainAlternates);
  const activeRoutingKey = useSelector(select.page.getActiveRoutingKey);
  const localesAlternateLinks = useSelector(select.site.getAlternateLinks);

  const isHomepage = activeRoutingKey === `${RoutingKey.Home}`;

  if (!isHomepage && homepageOnlyAlternatesEnabled) {
    return null;
  }

  if (crossDomainAlternatesEnabled) {
    return (
      <Helmet>
        {alternateTags.map(({ url, hrefLang }) => {
          const pathname =
            url.endsWith('/') && strippedPathname.startsWith('/')
              ? strippedPathname.replace('/', '')
              : strippedPathname;

          const href = `${url}${pathname}`;
          return <link key={hrefLang} rel="alternate" hrefLang={hrefLang} href={href} />;
        })}
      </Helmet>
    );
  }

  const customLinks = localesAlternateLinks?.filter(
    ({ link: { excluded_paths: excludedPaths } }) => {
      if (excludedPaths?.length) {
        const joinedExcludedPaths = excludedPaths
          .map((ep) => ep.path.replace(/(^\/)|(\/$)/g, ''))
          .join('|');
        const startWithExcludedPath = new RegExp(`^/(${joinedExcludedPaths})/.*`, 'g');
        return !startWithExcludedPath.test(strippedPathname);
      }

      return true;
    }
  );

  if (customLinks?.length) {
    return (
      <Helmet>
        {customLinks?.map(({ link: { locale_code: localeCode, base_url: baseUrl } }) => (
          <link
            key={localeCode}
            rel="alternate"
            hrefLang={localeCode}
            href={`${baseUrl}${strippedPathname}`}
          />
        ))}
      </Helmet>
    );
  }

  return (
    <Helmet>
      {locales?.map((locale, index) => {
        const isDefaultLocale = index === 0;
        const url = new URL(href);

        if (!isDefaultLocale) {
          url.set('pathname', `/${locale}${strippedPathname}`);
        }

        return (
          <link
            key={locale}
            rel="alternate"
            hrefLang={isDefaultLocale ? defaultHrefLang : locale}
            href={`${url.origin}${url.pathname}`}
          />
        );
      })}
    </Helmet>
  );
};

export default AlternateLinks;
